import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, AlertTriangle, CheckCircle, Clock, BarChart3 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ExampleRow {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  current_example_title: string | null;
  example_date: string | null;
  refresh_priority: number;
  refresh_status: string;
  refresh_review_due: string | null;
  is_current_example_active: boolean;
}

interface CourseStat {
  course_name: string;
  total: number;
  with_example: number;
  active: number;
  needs_refresh: number;
  review_soon: number;
  no_example_needed: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof CheckCircle }> = {
  current: { label: "Current", color: "text-emerald-500", icon: CheckCircle },
  review_soon: { label: "Review Soon", color: "text-yellow-500", icon: Clock },
  needs_refresh: { label: "Needs Refresh", color: "text-red-500", icon: AlertTriangle },
  archived: { label: "Archived", color: "text-muted-foreground", icon: RefreshCw },
  no_example_needed: { label: "Not Needed", color: "text-muted-foreground/50", icon: CheckCircle },
};

const PRIORITY_LABELS: Record<number, string> = { 1: "Fast (60-90d)", 2: "Moderate (6mo)", 3: "Slow (yearly)" };

export default function ExampleRefreshPanel() {
  const [examples, setExamples] = useState<ExampleRow[]>([]);
  const [stats, setStats] = useState<CourseStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("lessons")
        .select("lesson_id, lesson_name, course_name, current_example_title, example_date, refresh_priority, refresh_status, refresh_review_due, is_current_example_active")
        .order("course_name")
        .order("lesson_order");

      const rows = (data || []) as ExampleRow[];
      setExamples(rows);

      // Build per-course stats
      const courseMap = new Map<string, CourseStat>();
      rows.forEach((r) => {
        if (!courseMap.has(r.course_name)) {
          courseMap.set(r.course_name, { course_name: r.course_name, total: 0, with_example: 0, active: 0, needs_refresh: 0, review_soon: 0, no_example_needed: 0 });
        }
        const s = courseMap.get(r.course_name)!;
        s.total++;
        if (r.current_example_title) s.with_example++;
        if (r.is_current_example_active) s.active++;
        if (r.refresh_status === "needs_refresh") s.needs_refresh++;
        if (r.refresh_status === "review_soon") s.review_soon++;
        if (r.refresh_status === "no_example_needed") s.no_example_needed++;
      });
      setStats(Array.from(courseMap.values()));
      setLoading(false);
    };
    void load();
  }, []);

  if (loading) return <div className="text-sm text-muted-foreground p-4">Loading refresh data…</div>;

  const totalExamples = examples.filter((e) => e.current_example_title).length;
  const activeExamples = examples.filter((e) => e.is_current_example_active).length;
  const needsRefresh = examples.filter((e) => e.refresh_status === "needs_refresh").length;
  const reviewSoon = examples.filter((e) => e.refresh_status === "review_soon").length;

  // Fast-change queue: priority 1 that are due or stale
  const fastChangeQueue = examples
    .filter((e) => e.refresh_priority === 1 && (e.refresh_status === "needs_refresh" || e.refresh_status === "review_soon"))
    .sort((a, b) => (a.refresh_review_due || "").localeCompare(b.refresh_review_due || ""));

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "With Examples", value: totalExamples, total: examples.length },
          { label: "Active", value: activeExamples },
          { label: "Needs Refresh", value: needsRefresh, alert: needsRefresh > 0 },
          { label: "Review Soon", value: reviewSoon },
        ].map((s) => (
          <div key={s.label} className={cn("rounded-xl border border-border/60 bg-card/60 p-3 text-center", s.alert && "border-red-500/30")}>
            <p className={cn("font-heading text-lg font-bold", s.alert ? "text-red-500" : "text-foreground")}>
              {s.value}{s.total ? `/${s.total}` : ""}
            </p>
            <p className="text-[10px] text-muted-foreground font-heading uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="queue">
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="queue" className="text-xs gap-1"><AlertTriangle className="h-3 w-3" /> Refresh Queue</TabsTrigger>
          <TabsTrigger value="coverage" className="text-xs gap-1"><BarChart3 className="h-3 w-3" /> Coverage</TabsTrigger>
          <TabsTrigger value="all" className="text-xs gap-1"><RefreshCw className="h-3 w-3" /> All Examples</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="space-y-2 mt-3">
          {fastChangeQueue.length === 0 && (
            <div className="rounded-xl border border-border/60 bg-card/60 p-4 text-center">
              <CheckCircle className="h-5 w-5 text-emerald-500 mx-auto mb-1" />
              <p className="text-sm text-muted-foreground">No fast-change examples need attention right now.</p>
            </div>
          )}
          {fastChangeQueue.map((e) => {
            const cfg = STATUS_CONFIG[e.refresh_status] || STATUS_CONFIG.no_example_needed;
            const Icon = cfg.icon;
            return (
              <div key={e.lesson_id} className="rounded-xl border border-border/60 bg-card/60 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-heading text-xs font-semibold text-foreground truncate">{e.lesson_name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{e.course_name}</p>
                    {e.current_example_title && (
                      <p className="text-[10px] text-primary/70 mt-0.5 truncate">"{e.current_example_title}"</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Icon className={cn("h-3.5 w-3.5", cfg.color)} />
                    <span className={cn("text-[10px] font-heading font-semibold", cfg.color)}>{cfg.label}</span>
                  </div>
                </div>
                {e.refresh_review_due && (
                  <p className="text-[9px] text-muted-foreground mt-1">
                    Due: {new Date(e.refresh_review_due).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                )}
              </div>
            );
          })}

          {/* Also show non-fast-change items that need refresh */}
          {examples.filter((e) => e.refresh_priority !== 1 && e.refresh_status === "needs_refresh").length > 0 && (
            <>
              <div className="flex items-center gap-2 pt-2">
                <div className="h-px flex-1 bg-border" />
                <span className="text-[10px] font-heading uppercase text-muted-foreground">Other Stale Examples</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              {examples
                .filter((e) => e.refresh_priority !== 1 && e.refresh_status === "needs_refresh")
                .map((e) => (
                  <div key={e.lesson_id} className="rounded-xl border border-border/60 bg-card/60 p-3">
                    <p className="font-heading text-xs font-semibold text-foreground truncate">{e.lesson_name}</p>
                    <p className="text-[10px] text-muted-foreground">{e.course_name} · {PRIORITY_LABELS[e.refresh_priority] || "Unknown"}</p>
                  </div>
                ))}
            </>
          )}
        </TabsContent>

        <TabsContent value="coverage" className="space-y-3 mt-3">
          {stats.map((s) => {
            const coveragePct = s.total ? Math.round((s.with_example / s.total) * 100) : 0;
            const activePct = s.total ? Math.round((s.active / s.total) * 100) : 0;
            return (
              <div key={s.course_name} className="rounded-xl border border-border/60 bg-card/60 p-3">
                <p className="font-heading text-xs font-semibold text-foreground truncate mb-2">{s.course_name}</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground w-16 shrink-0">Has Ex.</span>
                    <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${coveragePct}%` }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground w-12 text-right">{s.with_example}/{s.total}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground w-16 shrink-0">Active</span>
                    <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${activePct}%` }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground w-12 text-right">{s.active}/{s.total}</span>
                  </div>
                </div>
                {(s.needs_refresh > 0 || s.review_soon > 0) && (
                  <div className="flex gap-3 mt-1.5 text-[10px]">
                    {s.needs_refresh > 0 && <span className="text-red-500 font-heading">{s.needs_refresh} stale</span>}
                    {s.review_soon > 0 && <span className="text-yellow-500 font-heading">{s.review_soon} review soon</span>}
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="all" className="space-y-2 mt-3 max-h-96 overflow-y-auto">
          {examples
            .filter((e) => e.current_example_title)
            .map((e) => {
              const cfg = STATUS_CONFIG[e.refresh_status] || STATUS_CONFIG.no_example_needed;
              const Icon = cfg.icon;
              return (
                <div key={e.lesson_id} className="rounded-xl border border-border/60 bg-card/60 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-heading text-xs font-semibold text-foreground truncate">{e.current_example_title}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{e.lesson_name} · {e.course_name}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Icon className={cn("h-3 w-3", cfg.color)} />
                      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[8px] font-heading">P{e.refresh_priority}</span>
                    </div>
                  </div>
                </div>
              );
            })}
        </TabsContent>
      </Tabs>
    </div>
  );
}
